/**
 * BackNavigation Component
 *
 * Displays a back navigation link with an arrow icon.
 * Use for navigating back to a previous page or section.
 *
 * @module components/common/BackNavigation
 */

import { memo } from 'react';
import { Link } from 'react-router-dom';
import { IconArrowLeft } from '@tabler/icons-react';
import type { BackNavigationProps } from './BackNavigation.types';
import styles from './BackNavigation.module.css';

/**
 * BackNavigation Component
 *
 * A simple back navigation link with an arrow icon.
 * Provides consistent styling for back navigation across the application.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <BackNavigation
 *   label="Back to Dashboard"
 *   href="/dashboard"
 * />
 *
 * // With custom icon
 * <BackNavigation
 *   label="Return to Tasks"
 *   href="/tasks"
 *   icon={<IconChevronLeft size={18} />}
 * />
 * ```
 */
export const BackNavigation = memo(function BackNavigation({
  label,
  href,
  icon,
  className,
}: BackNavigationProps) {
  const linkClass = [styles.link, className].filter(Boolean).join(' ');

  return (
    <Link to={href} className={linkClass}>
      <span className={styles.icon}>
        {icon || <IconArrowLeft size={18} />}
      </span>
      {label}
    </Link>
  );
});

BackNavigation.displayName = 'BackNavigation';
